import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import AccountDelegate from './account.delegate';
import {
    ERROR_MESSAGES,
    PASSWORD_MIN_LENGTH,
    REDIRECT_DELAY_MS,
    SUCCESS_MESSAGE,
} from './account.constants';

/** Splits a full name on whitespace; collapses runs like "John  Doe". */
function splitFullName(fullName) {
    const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: '', lastName: '' };
    const [firstName, ...rest] = parts;
    return { firstName, lastName: rest.join(' ') };
}

/**
 * @extends ClockoutController
 * @extends AccountDelegate
 */
class AccountController extends Inherit(ClockoutController, AccountDelegate) {
    _redirectTimerId = null;

    state = {
        user: {},
        loading: true,
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        showPasswordSection: false,
        saving: false,
        errorMessage: '',
        successMessage: '',
    };

    async initialize() {
        if (!this.user || !this.user.access || this.user.access.length === 0) {
            this.navigateLogin();
            return;
        }

        const user = this.data.user;
        const { firstName, lastName } = splitFullName(user?.name);

        this.setState({
            user,
            firstName,
            lastName,
            email: user?.email || '',
            phoneNumber: user?.phone || '',
            loading: false,
        });
    }

    async cleanup() {
        this.clearRedirectTimer();
    }

    clearRedirectTimer() {
        if (this._redirectTimerId != null) {
            clearTimeout(this._redirectTimerId);
            this._redirectTimerId = null;
        }
    }

    scheduleRedirect() {
        this.clearRedirectTimer();
        this._redirectTimerId = setTimeout(() => {
            this._redirectTimerId = null;
            this.navigateDashboard();
        }, REDIRECT_DELAY_MS);
    }

    /**
     * Returns the first validation error for the password change inputs, or
     * `null` when the inputs are valid. Centralised here so `handleSaveChanges`
     * stays a short orchestration flow.
     */
    validatePasswordSection() {
        const { currentPassword, newPassword, confirmPassword } = this.state;

        if (!currentPassword) return ERROR_MESSAGES.CURRENT_PASSWORD_REQUIRED;
        if (newPassword !== confirmPassword) return ERROR_MESSAGES.PASSWORDS_DO_NOT_MATCH;
        if (newPassword.length < PASSWORD_MIN_LENGTH) return ERROR_MESSAGES.PASSWORD_TOO_SHORT;

        return null;
    }

    /**
     * Returns `true` when any of the personal-info fields differ from what's
     * currently on `user`. Used to skip the `updateAccount` call when the
     * user only changed their password.
     */
    hasPersonalInfoChanges() {
        const { firstName, lastName, email, phoneNumber, user } = this.state;
        const currentFullName = `${firstName} ${lastName}`.trim();
        const originalFullName = user?.name || '';

        return (
            currentFullName !== originalFullName
            || email !== (user?.email || '')
            || phoneNumber !== (user?.phone || '')
        );
    }

    hasPasswordChanges() {
        const {
            showPasswordSection,
            currentPassword,
            newPassword,
            confirmPassword,
        } = this.state;

        return (
            showPasswordSection
            && Boolean(currentPassword || newPassword || confirmPassword)
        );
    }

    hasAccountChanges() {
        return this.hasPersonalInfoChanges() || this.hasPasswordChanges();
    }

    async handleSaveChanges() {
        let passwordUpdated = false;

        try {
            if (!this.hasAccountChanges()) return;

            this.setState({ saving: true, errorMessage: '', successMessage: '' });

            const { firstName, lastName, email, phoneNumber } = this.state;

            // Password and profile are separate API calls — not atomic. Password
            // runs first; if updateAccount fails afterward the password change
            // has already persisted (see PARTIAL_UPDATE_FAILED in the catch).
            if (this.hasPasswordChanges()) {
                const validationError = this.validatePasswordSection();
                if (validationError) {
                    this.setState({ errorMessage: validationError, saving: false });
                    return;
                }

                const { currentPassword, newPassword } = this.state;
                const passwordResponse = await this.data.updatePassword({
                    email,
                    password: newPassword,
                    current_password: currentPassword,
                });

                if (!passwordResponse?.success) {
                    throw new Error(passwordResponse?.message || ERROR_MESSAGES.UPDATE_FAILED);
                }

                passwordUpdated = true;
            }

            let updatedUser = this.state.user;

            if (this.hasPersonalInfoChanges()) {
                const updatedName = `${firstName} ${lastName}`.trim();
                const response = await this.data.updateAccount({
                    name: updatedName,
                    email,
                    phone: phoneNumber,
                });

                if (!response?.success) {
                    throw new Error(response?.message || ERROR_MESSAGES.UPDATE_FAILED);
                }

                updatedUser = {
                    ...this.state.user,
                    name: updatedName,
                    email,
                    phone: phoneNumber,
                };
                this.user = updatedUser;
                this.constructor._notifyAuthChange();
            }

            this.setState({
                user: updatedUser,
                saving: false,
                successMessage: SUCCESS_MESSAGE,
                showPasswordSection: false,
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });

            this.scheduleRedirect();
        } catch (error) {
            console.error('Error updating account:', error);
            this.setState({
                saving: false,
                successMessage: '',
                errorMessage: passwordUpdated
                    ? ERROR_MESSAGES.PARTIAL_UPDATE_FAILED
                    : (error.message || ERROR_MESSAGES.UPDATE_FAILED),
            });
        }
    }

    handleCancel() {
        this.navigateDashboard();
    }
}

export default AccountController;
